import { Link } from "react-router-dom";

/**
 * Reusable page hero header used across inner pages.
 * Follows architectural discipline: overline + h1 + hairline + placeholder body.
 */
export default function PageHeader({
  overline,
  title,
  subtitle,
  breadcrumb,
  variant = "light", // "light" | "dark"
  backgroundImage,
}) {
  const isDark = variant === "dark" || !!backgroundImage;
  return (
    <section
      data-testid="page-header"
      className={`relative overflow-hidden ${
        isDark ? "bg-navy text-white" : "bg-white text-navy"
      }`}
    >
      {/* Optional background image */}
      {backgroundImage && (
        <>
          <img
            src={backgroundImage}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-navy/95 via-navy/85 to-navy/75" />
        </>
      )}

      {/* Blueprint grid overlay */}
      <div
        aria-hidden
        className={`absolute inset-0 blueprint-grid ${
          isDark ? "opacity-30" : "opacity-100"
        }`}
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-8 md:col-start-1">
            {breadcrumb && (
              <nav
                aria-label="breadcrumb"
                className={`text-[11px] tracking-[0.25em] uppercase mb-6 ${
                  isDark ? "text-white/50" : "text-anthracite/60"
                }`}
              >
                <Link
                  to="/"
                  className="hover:text-gold transition-colors"
                  data-testid="breadcrumb-home"
                >
                  Ana Sayfa
                </Link>
                <span className="mx-2">/</span>
                <span className={isDark ? "text-gold" : "text-navy"}>{breadcrumb}</span>
              </nav>
            )}

            {overline && (
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-[1px] bg-gold" />
                <span
                  className={`overline ${
                    isDark ? "text-gold" : "text-anthracite/70"
                  }`}
                >
                  {overline}
                </span>
              </div>
            )}

            <h1
              data-testid="page-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]"
            >
              {title}
            </h1>

            {subtitle && (
              <p
                className={`mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light ${
                  isDark ? "text-white/70" : "text-anthracite/80"
                }`}
              >
                {subtitle}
              </p>
            )}
          </div>

          <div className="md:col-span-3 md:col-start-10 hidden md:flex flex-col items-end justify-end">
            <div
              className={`text-[10px] tracking-[0.3em] uppercase ${
                isDark ? "text-white/40" : "text-anthracite/50"
              }`}
            >
              1001 · Mimarlık
            </div>
            <div className="mt-3 w-24 h-[1px] bg-gold/70" />
          </div>
        </div>
      </div>

      {/* bottom hairline */}
      <div className="relative">
        <div className="h-[1px] hairline-x" />
      </div>
    </section>
  );
}
